import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Building2, LogOut } from 'lucide-react'
import { useAuth } from '../auth'
import { useI18n } from '../i18n'
import { supabase } from '../lib/supabase'
import { AvvRpcError, avvVorschau, createMyTenant, paketAnfrageGesehen, type AvvPartner, type AvvVorschau } from '../data/supabaseAdapter'
import { Button, Card, Field, Input, Select, cx } from '../ui/kit'
import { paketFuer, preisJeMonat, usePakete } from '../data/pakete'
import { kaufwegSichtbar } from '../lib/kaufweg'
import { ANBIETER } from '../lib/anbieter'
import { avvFehlerText, landName } from '../lib/avv'
import AvvSchritt, { gepruefteAvvAnnahme } from './AvvSchritt'

/**
 * First run. Creates the tenant plus a working starting set-up in one database call,
 * so the landlord lands on a calendar with priced units rather than an empty grid.
 */
export default function OnboardingView() {
  const { lang, formatMoney } = useI18n()
  const { setTenantId, signOut, session } = useAuth()
  const navigate = useNavigate()
  const { pakete } = usePakete()
  const de = lang === 'de'

  const [name, setName] = useState('')
  const [country, setCountry] = useState('AT')
  const [unitKind, setUnitKind] = useState<'apartment' | 'room'>('apartment')
  const [unitCount, setUnitCount] = useState(4)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [partner, setPartner] = useState<AvvPartner>({ firma: '', strasse: '', plz: '', ort: '', land: landName('AT') })
  const [partnerCountry, setPartnerCountry] = useState('AT')
  const [otherCountry, setOtherCountry] = useState('')
  const [avvPreview, setAvvPreview] = useState<AvvVorschau | null>(null)
  const [avvChecked, setAvvChecked] = useState(false)
  const [avvPending, setAvvPending] = useState(true)
  const [avvError, setAvvError] = useState<string | null>(null)
  const [avvReload, setAvvReload] = useState(0)
  const avvRevision = useRef(0)
  const updatePartner = (next: AvvPartner) => {
    avvRevision.current++
    setAvvChecked(false)
    setAvvPending(true)
    setAvvError(null)
    setPartner(next)
  }
  useEffect(() => {
    let active = true
    const revision = avvRevision.current
    setAvvPending(true)
    setAvvChecked(false)
    if (!supabase || !session) { setAvvPreview(null); setAvvPending(false); return }
    void avvVorschau(supabase, partner).then(v => {
      if (!active || revision !== avvRevision.current) return
      setAvvPreview(v)
      setAvvError(null)
    }).catch(err => {
      if (!active || revision !== avvRevision.current) return
      const fieldError = err instanceof AvvRpcError && ['avv_vertragspartner_unvollstaendig', 'avv_vertragspartner_zu_lang', 'avv_vertragspartner_zeichen', 'avv_platzhalter_offen'].includes(err.kennung)
      // Field errors keep editable partner fields, never stale acceptable bytes.
      setAvvPreview(v => fieldError && v ? { ...v, text: null, sha256: null } : null)
      setAvvError(err instanceof AvvRpcError ? avvFehlerText(err.kennung, err.hint, lang)
        : (de ? 'Der Vertrag konnte nicht geladen werden. Bitte versuchen Sie es erneut.' : 'The agreement could not be loaded. Please try again.'))
    }).finally(() => { if (active && revision === avvRevision.current) setAvvPending(false) })
    return () => { active = false }
  }, [partner, avvReload, session, lang, de])
  const avvReady = !avvPending && (!avvPreview || (avvChecked && !!avvPreview.text && !!avvPreview.sha256 && avvPreview.fehlend.length === 0))


  /*
   * Das Paket folgt der angegebenen Einheitenzahl, und der Preis dazu steht auf
   * dem Bildschirm, BEVOR abgesendet wird. Ein Vermieter soll dem Preis
   * begegnen, bevor er dem Produkt begegnet.
   *
   * Über der Selbstbedienungsgrenze — also dort, wo es ein Paket, aber keinen
   * Listenpreis gibt — wird nicht abgesendet, sondern gefragt. Die Grenze steht
   * dabei NICHT hier im Code: sie ergibt sich daraus, wofür es ein Paket mit
   * Preis gibt. Verschiebt jemand die Bänder in `plans`, folgt diese Seite ohne
   * Auslieferung. Die Datenbank lehnt denselben Fall noch einmal ab
   * (`plan_offer_required`) — eine Regel, die nur in der Oberfläche steht, gilt
   * nicht für den nächsten Aufrufer.
   */
  const paket = paketFuer(pakete, unitCount)
  const jeMonat = paket ? preisJeMonat(paket, 'yearly') : null
  const jeMonatAbo = paket ? preisJeMonat(paket, 'monthly') : null
  const aufAnfrage = paket !== null && jeMonat === null

  /*
   * Der wertvollste Fall des ganzen Vorgangs, und der einzige, der ohne
   * Mandanten stattfindet: ein Betrieb gibt in der ersten Minute eine Zahl an,
   * für die es kein Paket mit Preis gibt. Bisher hinterließ er keine Spur —
   * `create_my_tenant()` lehnt ab, und die Sperre selbst kann nichts
   * protokollieren, weil ein `raise exception` seine eigene Transaktion
   * zurückrollt.
   *
   * Festgehalten wird deshalb hier, beim ersten Erscheinen der Aussage, mit
   * `tenant_id = null`. Einmal je Aufruf dieser Seite: die Zahl im Feld ändert
   * sich beim Tippen, der Interessent ist derselbe. Ohne Mandant ist das eine
   * EREIGNISZAHL und keine Personenzahl — es gibt in diesem Moment noch keine
   * Kennung, und das steht auch an der Auswertung in Migration 0063.
   *
   * Ein Fehler wird verschluckt: eine Messung darf niemandem den Weg
   * verstellen, schon gar nicht in der ersten Minute.
   */
  const gemerkt = useRef(false)
  useEffect(() => {
    if (!aufAnfrage || gemerkt.current || !supabase) return
    gemerkt.current = true
    void paketAnfrageGesehen(supabase, null, unitCount).catch(() => {})
  }, [aufAnfrage, unitCount])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!supabase || busy || !avvReady) return
    const revision = avvRevision.current
    setBusy(true)
    setError(null)
    try {
      const acceptance = await gepruefteAvvAnnahme(avvPreview, partner, avvChecked)
      if (revision !== avvRevision.current || (avvPreview && !acceptance)) { setBusy(false); return }
      const id = await createMyTenant(supabase, {
        name: name.trim(), country, locale: lang, unitCount, unitKind,
        ...(acceptance ? { avv: acceptance } : {}),
      })
      setTenantId(id)
      /*
       * Im Browser weiter zu Testbedingungen und Zahlungsdaten: ein Vermieter
       * soll dem Preis begegnen, bevor er dem Produkt begegnet, nicht danach.
       *
       * In der App-Hülle gibt es diesen Weg nicht (siehe `lib/kaufweg.ts`), und
       * ein Sprung auf eine Route, die dort nicht registriert ist, landete auf
       * der Fehlerseite. Also direkt ins Produkt.
       */
      navigate(kaufwegSichtbar() ? '/billing/setup' : '/', { replace: true })
    } catch (err) {
      if (err instanceof AvvRpcError || (err instanceof Error && err.message === 'avv_hash_abweichend')) {
        const code = err instanceof AvvRpcError ? err.kennung : 'avv_hash_abweichend'
        setError(avvFehlerText(code, err instanceof AvvRpcError ? err.hint : null, lang))
        setAvvChecked(false)
        avvRevision.current++
        setAvvReload(n => n + 1)
      } else setError(err instanceof Error ? err.message : String(err))
      setBusy(false)
    }
  }

  return (
    <div className="min-h-dvh flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-4">
        <div className="text-center">
          <div className="w-14 h-14 rounded-2xl bg-accent text-accent-fg mx-auto
                          flex items-center justify-center mb-3">
            <Building2 size={26} />
          </div>
          <h1 className="text-xl font-semibold">
            {de ? 'Willkommen' : 'Welcome'}
          </h1>
          <p className="text-sm text-muted mt-1">
            {kaufwegSichtbar()
              ? (de
                ? '30 Tage kostenlos testen. Was es danach kostet, hängt von der Zahl Ihrer Einheiten ab und steht weiter unten. Im nächsten Schritt hinterlegen Sie Ihre Zahlungsdaten — abgebucht wird erst nach der Testphase.'
                : 'Free for 30 days. What it costs afterwards depends on how many units you have and is shown below. You add payment details in the next step — nothing is charged until the trial ends.')
              : (de
                ? '30 Tage kostenlos testen.'
                : 'Free for 30 days.')}
          </p>
        </div>

        <Card className="p-4">
          <form onSubmit={submit} className="space-y-3">
            <Field
              label={de ? 'Name des Betriebs' : 'Business name'}
              hint={de ? 'Erscheint auf Angeboten und Rechnungen' : 'Appears on quotes and invoices'}
            >
              <Input
                required autoFocus value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={de ? 'Haus Bergblick' : 'Rose Cottage'}
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label={de ? 'Land' : 'Country'}>
                <Select value={country} onChange={(e) => {
                  const code = e.target.value
                  setCountry(code); setPartnerCountry(code); setOtherCountry('')
                  updatePartner({ ...partner, land: landName(code) })
                }}>
                  <option value="AT">Österreich</option>
                  <option value="DE">Deutschland</option>
                  <option value="CH">Schweiz</option>
                  <option value="IT">Italia</option>
                  <option value="GB">United Kingdom</option>
                  <option value="IE">Ireland</option>
                  <option value="OTHER">{de ? 'Anderes' : 'Other'}</option>
                </Select>
              </Field>
              <Field label={de ? 'Art' : 'Type'}>
                <Select
                  value={unitKind}
                  onChange={(e) => setUnitKind(e.target.value as 'apartment' | 'room')}
                >
                  <option value="apartment">{de ? 'Ferienwohnungen' : 'Holiday flats'}</option>
                  <option value="room">{de ? 'Zimmer' : 'Rooms'}</option>
                </Select>
              </Field>
            </div>

            <Field
              label={de ? 'Wie viele Einheiten?' : 'How many units?'}
              hint={de ? 'Lässt sich später jederzeit ändern' : 'You can change this at any time'}
            >
              <div className="flex gap-2 flex-wrap">
                {[2, 4, 6, 8, 10].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setUnitCount(n)}
                    className={cx(
                      'w-12 h-12 rounded-xl text-sm font-medium border',
                      unitCount === n
                        ? 'bg-accent text-accent-fg border-accent'
                        : 'bg-surface-2 text-muted border-border',
                    )}
                  >
                    {n}
                  </button>
                ))}
                <Input
                  type="number" min={1} max={50}
                  className="w-20"
                  value={unitCount}
                  onChange={(e) => setUnitCount(Math.min(50, Math.max(1, Number(e.target.value) || 1)))}
                />
              </div>
            </Field>

            {/* Welches Paket das ist und was es kostet — vor dem Absenden. */}
            {paket && !aufAnfrage && (
              <p className="text-sm text-muted">
                {de ? paket.name.de : paket.name.en ?? paket.name.de}
                {kaufwegSichtbar() && jeMonat !== null && jeMonatAbo !== null && (
                  de
                    ? ` · ${formatMoney(jeMonat, paket.currency)} je Monat im Jahresvertrag, ${formatMoney(jeMonatAbo, paket.currency)} im Monatsabo, zzgl. USt.`
                    : ` · ${formatMoney(jeMonat, paket.currency)} per month on the annual contract, ${formatMoney(jeMonatAbo, paket.currency)} monthly, plus VAT.`
                )}
              </p>
            )}

            {/*
              * Kein roter Streifen und keine Fehlermeldung: das hier ist eine
              * Qualifizierung, keine Absage — und es ist die erste Minute mit
              * dem Produkt. Alles Eingetippte bleibt stehen.
              */}
            {aufAnfrage && (
              <div className="text-sm bg-surface-2 rounded-xl px-3 py-2 space-y-2">
                <p>
                  {de
                    ? 'StaySuite ist ein Produkt für kleine Privatvermieter. Für einen Betrieb Ihrer Größe gibt es kein Listenpaket, sondern ein individuelles Angebot.'
                    : 'StaySuite is built for small private landlords. For a business of your size there is no list plan, but an individual offer.'}
                </p>
                {/*
                  * Der Weg nach draußen, im Browser und nur dort — dieselbe
                  * Adresse wie im Impressum, und ohne genannte Frist: zugesagt
                  * hat sie niemand.
                  */}
                {kaufwegSichtbar() && (
                  <p className="text-muted">
                    {de
                      ? `Schreiben Sie uns an ${ANBIETER.mail}, warum ausgerechnet eine App für Privatvermieter für Sie interessant ist — mit Ihrer Einheitenzahl.`
                      : `Write to ${ANBIETER.mail} and tell us why an app for private landlords is the right one for you — with your number of units.`}
                  </p>
                )}
              </div>
            )}

            <AvvSchritt preview={avvPreview} partner={partner} countryCode={partnerCountry}
              otherCountry={otherCountry} checked={avvChecked} pending={avvPending} de={de} error={avvError}
              onPartner={updatePartner}
              onCountry={code => { setPartnerCountry(code); updatePartner({ ...partner, land: landName(code, otherCountry) }) }}
              onOtherCountry={text => { setOtherCountry(text); updatePartner({ ...partner, land: landName('OTHER', text) }) }}
              onChecked={value => {
                // An acceptance withdrawn while hashing must invalidate the pending submit.
                avvRevision.current++
                setAvvChecked(value)
              }} />
            {avvError && !avvPreview && <Button type="button" variant="ghost" size="sm" onClick={() => setAvvReload(n => n + 1)}>{de ? 'Vertrag erneut laden' : 'Reload agreement'}</Button>}

            {error && (
              <p className="text-sm bg-danger-soft text-danger rounded-xl px-3 py-2">{error}</p>
            )}

            <Button type="submit" variant="primary" className="w-full"
              disabled={busy || !name.trim() || aufAnfrage || !avvReady}>
              {busy
                ? (de ? 'Wird eingerichtet …' : 'Setting up …')
                : (de ? 'Kalender einrichten' : 'Set up my calendar')}
            </Button>
          </form>
        </Card>

        <div className="text-center">
          <p className="text-xs text-muted mb-2">{session?.user.email}</p>
          <Button variant="ghost" size="sm" onClick={signOut}>
            <LogOut size={14} /> {de ? 'Abmelden' : 'Sign out'}
          </Button>
        </div>
      </div>
    </div>
  )
}
